/**
 * Pure domain models and builders for Consolidated Radar Work Threads and Pulse Metrics.
 */

import type { ManualWorkHub, ResourceRelation } from '@zunelo/shared';
import {
  AttentionItem,
  AgentItem,
  NoteItem,
  ReminderItem,
  SourceType,
  Tone,
  ViewMode,
} from './types';
import { assignRadarLane } from './radarLanes';
import {
  assignAgentToGroup,
  assignItemToGroup,
  buildThreadFromGroup,
  emptyTicketGroup,
  type TicketGroup,
} from './workThreadBuilders';
import { clusterAttentionIntoWorkContexts } from './workContextBridge';
import { attachSlackMentionedSources } from './radarMentionLinks';
import type { FilterAxis } from './multiAxisFiltering';

export type WorkThreadSourceKind = SourceType | 'notes' | 'reminders';

export interface WorkThreadSourceStatus {
  source: WorkThreadSourceKind;
  label: string;
  detail: string;
  tone?: Tone;
  /** Optional AI brief from the linked attention item (never raw description). */
  aiBrief?: string;
}

export interface WorkThreadAction {
  id: string;
  label: string;
  source: SourceType;
  variant: 'primary' | 'secondary' | 'tertiary';
  targetId: string;
  targetView: ViewMode;
}

export interface WorkThread {
  id: string;
  key: string;
  title: string;
  description?: string;
  status: 'you' | 'them' | 'blocked' | 'ready';
  statusLabel: string;
  statusTone: Tone;
  updatedText: string;
  updatedAt: string;
  sources: WorkThreadSourceStatus[];
  aiDiagnostic: string;
  nextStep: string;
  actions: WorkThreadAction[];
  priorityRank: number;
  isManual: boolean;
  memberCount: number;
  hasUnread: boolean;
  /** Pulse lanes represented by members — same mapping as Briefing de hoy counts. */
  briefingBuckets: BriefingBucketId[];
}

export type RadarThreadSortMode = 'priority' | 'unread' | 'recency';

function isRadarActionableItem(item: AttentionItem): boolean {
  if (item.source === 'jira') {
    const workflow = item.workflow?.trim().toLowerCase();
    return (
      item.type !== 'Epic' && !['done', 'cerrado', 'closed', 'resolved'].includes(workflow ?? '')
    );
  }
  if (item.source === 'github') {
    return !item.merged && item.rawPullRequest?.state !== 'closed';
  }
  if (item.source === 'gmail') {
    return item.unread === true || item.blocksWork === true;
  }
  return true;
}

export function radarActionableItems(items: readonly AttentionItem[]): AttentionItem[] {
  return items.filter(isRadarActionableItem);
}

export interface RadarPulseMetrics {
  inProgressCount: number;
  waitingYouCount: number;
  readyCount: number;
  stuckCount: number;
  nextReminder?: {
    time: string;
    text: string;
    dayLabel: string;
  };
}

/** Briefing de hoy presentation derived from pulse lane counters. */
export type BriefingBucketId = 'require-attention' | 'watching' | 'blockers' | 'under-control';
export type BriefingBucketFilter = BriefingBucketId | 'all';

export const BRIEFING_HIGHLIGHT_PREVIEW = 3;
export const BRIEFING_HIGHLIGHT_MAX = 12;

export interface RadarBriefingBucket {
  id: BriefingBucketId;
  count: number;
  title: string;
  subtitle: string;
  tone: 'bad' | 'warn' | 'accent' | 'ok';
}

export interface RadarBriefingHighlight {
  id: string;
  source: SourceType;
  title: string;
  detail: string;
  age: string;
  targetView: ViewMode;
}

export interface RadarBriefingModel {
  updatedLabel: string;
  attentionSentence: string;
  buckets: RadarBriefingBucket[];
  highlights: RadarBriefingHighlight[];
  /** Legacy pulse counters retained for tools / reminders affordance. */
  pulse: RadarPulseMetrics;
}

export type ContextAffiliation = 'mine' | 'theirs';
export type ContextPriorityChip = 'urgent' | 'important' | null;

export interface WorkThreadSourceChip {
  source: WorkThreadSourceKind;
}

export function contextAffiliation(thread: WorkThread): ContextAffiliation {
  return thread.status === 'you' || thread.hasUnread ? 'mine' : 'theirs';
}

export function contextPriorityChip(thread: WorkThread): ContextPriorityChip {
  if (thread.status === 'you') return 'urgent';
  if (thread.status === 'blocked' || thread.statusTone === 'warn') return 'important';
  return null;
}

export function contextSourceChips(thread: WorkThread): WorkThreadSourceChip[] {
  return thread.sources.map((src) => ({
    source: src.source,
  }));
}

export function primaryRespondAction(thread: WorkThread): WorkThreadAction | undefined {
  return thread.actions.find((act) => act.variant === 'primary');
}

/** Context chip attached to the Radar AI assistant (like a chat attachment). */
export interface RadarAiAttachment {
  id: string;
  key: string;
  title: string;
  summary: string;
  nextStep: string;
  sources: WorkThreadSourceKind[];
}

export function toRadarAiAttachment(thread: WorkThread): RadarAiAttachment {
  return {
    id: thread.id,
    key: thread.key,
    title: thread.title,
    summary: buildCrossAiSummary(thread),
    nextStep: thread.nextStep,
    sources: thread.sources.map((src) => src.source),
  };
}

export function upsertRadarAiAttachment(
  attachments: RadarAiAttachment[],
  next: RadarAiAttachment,
): RadarAiAttachment[] {
  if (attachments.some((item) => item.id === next.id)) return attachments;
  return [...attachments, next];
}

export function removeRadarAiAttachment(
  attachments: RadarAiAttachment[],
  id: string,
): RadarAiAttachment[] {
  return attachments.filter((item) => item.id !== id);
}

/**
 * Client-side grounded reply for the Radar AI panel (no live LLM yet).
 */
export function buildRadarAiReply(
  query: string,
  attachments: RadarAiAttachment[],
  fallback?: RadarAiAttachment,
): string {
  const q = query.trim();
  const pack = attachments.length > 0 ? attachments : fallback ? [fallback] : [];
  if (!q) {
    return 'Escribe una pregunta o elige una sugerencia para continuar.';
  }
  if (pack.length === 0) {
    return `Sobre «${q}»: no hay contextos adjuntos todavía. Usa «Pasar al asistente» en una card o el clip del panel para añadir contexto.`;
  }
  const keys = pack.map((item) => item.key).join(', ');
  const digests = pack
    .slice(0, 3)
    .map((item) => `${item.key}: ${item.summary}`)
    .join(' ');
  const next = pack[0]!.nextStep;
  const lower = q.toLowerCase();
  if (lower.includes('bloque') || lower.includes('riesgo')) {
    return `Con ${keys} en contexto: ${digests} Riesgo principal a vigilar y siguiente paso: ${next}`;
  }
  if (lower.includes('resumen') || lower.includes('resume')) {
    return `Resumen con adjuntos (${keys}): ${digests} Qué sigue: ${next}`;
  }
  if (lower.includes('redact') || lower.includes('respuesta') || lower.includes('slack')) {
    return `Borrador apoyado en ${keys}: «Hola — respecto a ${pack[0]!.title}, el estado es: ${pack[0]!.summary} Propongo: ${next}»`;
  }
  return `Sobre «${q}» con ${keys}: ${digests} Recomendación: ${next}`;
}

const RADAR_PLATFORM_OPTIONS: Array<{ source: WorkThreadSourceKind; label: string }> = [
  { source: 'jira', label: 'Jira' },
  { source: 'github', label: 'GitHub' },
  { source: 'slack', label: 'Slack' },
  { source: 'notes', label: 'Notas' },
  { source: 'reminders', label: 'Recordatorios' },
];

/** Radar's filter axes on the shared multi-axis filtering engine. */
export const RADAR_AXES: FilterAxis<WorkThread>[] = [
  {
    id: 'affiliation',
    label: 'Afiliación',
    type: 'single',
    options: [
      { value: 'mine', label: 'Mía', predicate: (t) => contextAffiliation(t) === 'mine' },
      { value: 'theirs', label: 'Ajena', predicate: (t) => contextAffiliation(t) === 'theirs' },
    ],
  },
  {
    id: 'priority',
    label: 'Prioridad',
    type: 'single',
    options: [
      { value: 'urgent', label: 'Urgente', predicate: (t) => contextPriorityChip(t) === 'urgent' },
      {
        value: 'important',
        label: 'Importante',
        predicate: (t) => contextPriorityChip(t) === 'important',
      },
    ],
  },
  {
    id: 'platforms',
    label: 'Plataformas',
    type: 'multi',
    options: RADAR_PLATFORM_OPTIONS.map(({ source, label }) => ({
      value: source,
      label,
      predicate: (t) => t.sources.some((src) => src.source === source),
    })),
  },
];

export function filterThreadsByBriefingBucket(
  threads: readonly WorkThread[],
  bucket: BriefingBucketFilter,
): WorkThread[] {
  if (bucket === 'all') return [...threads];
  return threads.filter((thread) => thread.briefingBuckets.includes(bucket));
}

const PLATFORM_FOCUS_LABEL: Partial<Record<WorkThreadSourceKind, string>> = {
  jira: 'Jira',
  github: 'GitHub',
  slack: 'Slack',
  gmail: 'Gmail',
  agents: 'Agente',
  notes: 'Notas',
  reminders: 'Recordatorios',
};

/**
 * Synthesized cross-source IA brief. MUST NOT return raw card description alone.
 */
export function buildCrossAiSummary(thread: WorkThread): string {
  const withAi = thread.sources.filter((src) => Boolean(src.aiBrief?.trim()));
  if (withAi.length >= 2) {
    const joined = withAi
      .slice(0, 4)
      .map((src) => `${src.label}: ${src.aiBrief!.trim()}`)
      .join(' ');
    return `${joined} Siguiente paso sugerido: ${thread.nextStep}`;
  }
  if (withAi.length === 1) {
    const focused = withAi[0]!;
    const others = thread.sources
      .filter((src) => src.source !== focused.source)
      .map((src) => src.label);
    const related = others.length > 0 ? ` Relacionado con ${others.join(', ')}.` : '';
    return `${focused.label} indica: ${focused.aiBrief!.trim()}.${related} ${thread.aiDiagnostic}`.trim();
  }
  const platforms = thread.sources.map((src) => src.label);
  if (platforms.length > 0) {
    return `Hay actividad cruzada en ${platforms.join(', ')} para ${thread.key}. Estado: ${thread.statusLabel}. ${thread.nextStep}`;
  }
  return thread.aiDiagnostic || 'Sin señales cruzadas adicionales.';
}

/**
 * Platform-focused IA brief for the selected source tab.
 */
export function buildPlatformAiSummary(thread: WorkThread, source: WorkThreadSourceKind): string {
  const status = thread.sources.find((src) => src.source === source);
  const label = PLATFORM_FOCUS_LABEL[source] || source;
  if (!status) {
    return `No hay señales de ${label} en este contexto.`;
  }
  const brief = status.aiBrief?.trim();
  if (brief) {
    return `Enfoque ${status.label}: ${brief} Señal actual: ${status.detail}.`;
  }
  return `Enfoque ${status.label}: ${status.detail}. Lectura del contexto: ${thread.aiDiagnostic}`;
}

export function buildRadarBriefing(
  items: AttentionItem[],
  agents: AgentItem[],
  reminders: ReminderItem[],
  options: { nowMs?: number; syncedAtMs?: number } = {},
): RadarBriefingModel {
  const pulse = computeRadarPulseMetrics(items, agents, reminders);
  const syncedAtMs = options.syncedAtMs ?? options.nowMs ?? Date.now();
  const nowMs = options.nowMs ?? Date.now();
  const ageMinutes = Math.max(0, Math.round((nowMs - syncedAtMs) / 60_000));
  const updatedLabel = ageMinutes <= 0 ? 'Actualizado ahora' : `Actualizado hace ${ageMinutes} min`;

  const waitingCount = pulse.waitingYouCount;
  const attentionSentence =
    waitingCount === 1
      ? 'Tienes 1 asunto que requiere tu respuesta y podría escalar'
      : `Tienes ${waitingCount} asuntos que requieren tu respuesta y podrían escalar`;

  const buckets: RadarBriefingBucket[] = [
    {
      id: 'require-attention',
      count: pulse.waitingYouCount,
      title: 'Requieren atención',
      subtitle: 'Se te pueden pasar',
      tone: 'bad',
    },
    {
      id: 'watching',
      count: pulse.inProgressCount,
      title: 'En observación',
      subtitle: 'Podrían requerirte',
      tone: 'warn',
    },
    {
      id: 'blockers',
      count: pulse.stuckCount,
      title: 'Bloqueos / riesgos',
      subtitle: 'Impacto en el avance',
      tone: 'accent',
    },
    {
      id: 'under-control',
      count: pulse.readyCount,
      title: 'Sin acción',
      subtitle: 'Todo bajo control',
      tone: 'ok',
    },
  ];

  const highlights = missableHighlights(items, nowMs);

  return { updatedLabel, attentionSentence, buckets, highlights, pulse };
}

function missableHighlights(items: AttentionItem[], nowMs: number): RadarBriefingHighlight[] {
  return items
    .filter((item) => assignRadarLane(item) === 'waiting-you')
    .sort((left, right) => missableScore(right, nowMs) - missableScore(left, nowMs))
    .slice(0, BRIEFING_HIGHLIGHT_MAX)
    .map((item) => ({
      id: item.id,
      source: item.source,
      title: item.title || item.primary || item.id,
      detail: item.ai || item.secondary || item.title || item.primary || item.id,
      age: item.age || 'hace poco',
      targetView: viewForSource(item.source),
    }));
}

function missableScore(item: AttentionItem, nowMs: number): number {
  let score = 0;
  if (item.source === 'slack') score += slackMissableBonus(item);
  if (item.unread) score += 15;
  return score + missableAgeBonus(item.updatedAt, nowMs);
}

function slackMissableBonus(item: AttentionItem): number {
  if (item.tags?.includes('Te nombraron')) return 120;
  if (item.tags?.includes('Menciones')) return 110;
  return 40;
}

function missableAgeBonus(updatedAt: string | undefined, nowMs: number): number {
  if (!updatedAt) return 0;
  const updatedMs = Date.parse(updatedAt);
  if (!Number.isFinite(updatedMs)) return 0;
  const ageMinutes = Math.max(0, (nowMs - updatedMs) / 60_000);
  return Math.min(50, ageMinutes / 6);
}

function viewForSource(source: SourceType): ViewMode {
  if (
    source === 'jira' ||
    source === 'slack' ||
    source === 'github' ||
    source === 'gmail' ||
    source === 'agents'
  ) {
    return source;
  }
  return 'radar';
}

export interface PartitionedWorkThreads {
  /** Consolidated work contexts (2+ members or a manual hub). */
  contexts: WorkThread[];
  /** Singleton source cards not yet attached to a multi-member context. */
  withoutContext: WorkThread[];
}

/** Membership of an attention card in a Radar work context (for source tabs). */
export interface AttentionItemWorkContextMembership {
  id: string;
  key: string;
  title: string;
  isManual: boolean;
  isConsolidated: boolean;
}

const LANE_COUNTERS: Record<
  string,
  (counts: {
    inProgressCount: number;
    waitingYouCount: number;
    readyCount: number;
    stuckCount: number;
  }) => void
> = {
  now: (counts) => {
    counts.inProgressCount++;
  },
  'waiting-you': (counts) => {
    counts.waitingYouCount++;
  },
  ready: (counts) => {
    counts.readyCount++;
  },
  stuck: (counts) => {
    counts.stuckCount++;
  },
};

function countLane(
  lane: ReturnType<typeof assignRadarLane>,
  counts: {
    inProgressCount: number;
    waitingYouCount: number;
    readyCount: number;
    stuckCount: number;
  },
): void {
  if (!lane) return;
  LANE_COUNTERS[lane]?.(counts);
}

function nextReminderFromList(reminders: ReminderItem[]): RadarPulseMetrics['nextReminder'] {
  const activeReminders = reminders.filter((r) => !r.done);
  if (activeReminders.length === 0) return undefined;
  const first = activeReminders[0];
  return {
    time: first.time || '14:00',
    text: first.text,
    dayLabel: 'Hoy',
  };
}

/**
 * Computes the 5 top-level KPI metrics for the Radar Pulse bar.
 */
export function computeRadarPulseMetrics(
  items: AttentionItem[],
  agents: AgentItem[],
  reminders: ReminderItem[],
): RadarPulseMetrics {
  const counts = { inProgressCount: 0, waitingYouCount: 0, readyCount: 0, stuckCount: 0 };

  for (const item of items) {
    countLane(assignRadarLane(item), counts);
  }

  for (const agent of agents) {
    if (agent.running) counts.inProgressCount++;
    else counts.waitingYouCount++;
  }

  return {
    ...counts,
    nextReminder: nextReminderFromList(reminders),
  };
}

function ticketGroupFromCluster(
  items: AttentionItem[],
  agents: AgentItem[],
  notes: NoteItem[],
  reminders: ReminderItem[],
): TicketGroup {
  const group = emptyTicketGroup();
  for (const item of items) assignItemToGroup(group, item);
  for (const agent of agents) assignAgentToGroup(group, agent);
  group.notes.push(...notes);
  group.reminders.push(...reminders);
  return group;
}

export interface BuildWorkThreadsOptions {
  extraRelations?: readonly ResourceRelation[];
  manualHubs?: readonly ManualWorkHub[];
  notes?: readonly NoteItem[];
  reminders?: readonly ReminderItem[];
}

function isRelationList(
  value: BuildWorkThreadsOptions | readonly ResourceRelation[],
): value is readonly ResourceRelation[] {
  return Array.isArray(value);
}

/**
 * Groups items and agents into consolidated WorkThreads via the WorkContext
 * relation graph (primary ticket hub / first ref / manual hubs).
 */
export function buildWorkThreads(
  items: AttentionItem[],
  agents: AgentItem[],
  options: BuildWorkThreadsOptions | readonly ResourceRelation[] = {},
): WorkThread[] {
  const opts: BuildWorkThreadsOptions = isRelationList(options)
    ? { extraRelations: options }
    : options;
  const clusters = clusterAttentionIntoWorkContexts(items, agents, {
    extraRelations: opts.extraRelations,
    manualHubs: opts.manualHubs,
    notes: opts.notes,
    reminders: opts.reminders,
  });
  const threads: WorkThread[] = [];
  for (const cluster of clusters) {
    const group = ticketGroupFromCluster(
      cluster.items,
      cluster.agents,
      cluster.notes,
      cluster.reminders,
    );
    attachSlackMentionedSources(group, items);
    const thread = buildThreadFromGroup(group, cluster.displayKey, {
      title: cluster.manualTitle,
      isManual: cluster.isManual,
    });
    if (thread) threads.push(thread);
  }
  return sortWorkThreads(threads, 'priority');
}

/** True when the thread belongs in the Contextos section. */
export function isConsolidatedWorkThread(thread: WorkThread): boolean {
  return thread.isManual || thread.memberCount >= 2;
}

export function partitionWorkThreads(threads: readonly WorkThread[]): PartitionedWorkThreads {
  const contexts: WorkThread[] = [];
  const withoutContext: WorkThread[] = [];
  for (const thread of threads) {
    if (isConsolidatedWorkThread(thread)) contexts.push(thread);
    else withoutContext.push(thread);
  }
  return { contexts, withoutContext };
}

function membershipFromCluster(cluster: {
  displayKey: string;
  manualTitle?: string;
  isManual: boolean;
  items: readonly AttentionItem[];
  agents: readonly AgentItem[];
  notes: readonly NoteItem[];
  reminders: readonly ReminderItem[];
}): AttentionItemWorkContextMembership {
  const memberCount =
    cluster.items.length + cluster.agents.length + cluster.notes.length + cluster.reminders.length;
  return {
    id: cluster.displayKey,
    key: cluster.displayKey,
    title: cluster.manualTitle || cluster.displayKey,
    isManual: cluster.isManual,
    isConsolidated: cluster.isManual || memberCount >= 2,
  };
}

/**
 * Maps each attention item id → the work context (contexto) it belongs to.
 * Source tabs use this to show affiliation chips on Jira / GitHub / Slack cards.
 */
export function indexAttentionItemWorkContexts(
  items: readonly AttentionItem[],
  agents: readonly AgentItem[] = [],
  options: BuildWorkThreadsOptions = {},
): ReadonlyMap<string, AttentionItemWorkContextMembership> {
  const clusters = clusterAttentionIntoWorkContexts(items, agents, {
    extraRelations: options.extraRelations,
    manualHubs: options.manualHubs,
    notes: options.notes,
    reminders: options.reminders,
  });
  const map = new Map<string, AttentionItemWorkContextMembership>();
  for (const cluster of clusters) {
    const membership = membershipFromCluster(cluster);
    for (const item of cluster.items) {
      map.set(item.id, membership);
    }
  }
  return map;
}

/** Full cross-source context snapshot used by the side inspector. */
export interface AttentionItemContextSnapshot {
  thread: WorkThread;
  notes: NoteItem[];
  reminders: ReminderItem[];
  isManual: boolean;
  manualTitle?: string;
}

/**
 * Resolves the full context snapshot for a single attention card: the
 * consolidated WorkThread (Jira / GitHub / Slack / Agents + notes + reminders)
 * plus the raw notes and reminders attached to that context.
 * @returns undefined when the item is not part of the provided set.
 */
export function itemWorkContextSnapshot(
  item: AttentionItem,
  items: readonly AttentionItem[],
  agents: readonly AgentItem[] = [],
  options: BuildWorkThreadsOptions = {},
): AttentionItemContextSnapshot | undefined {
  const clusters = clusterAttentionIntoWorkContexts(items, agents, options);
  const cluster = clusters.find((candidate) =>
    candidate.items.some((member) => member.id === item.id),
  );
  if (!cluster) return undefined;
  const group = ticketGroupFromCluster(
    cluster.items,
    cluster.agents,
    cluster.notes,
    cluster.reminders,
  );
  attachSlackMentionedSources(group, items);
  const thread = buildThreadFromGroup(group, cluster.displayKey, {
    title: cluster.manualTitle,
    isManual: cluster.isManual,
  });
  if (!thread) return undefined;
  return {
    thread,
    notes: cluster.notes,
    reminders: cluster.reminders,
    isManual: cluster.isManual,
    manualTitle: cluster.manualTitle,
  };
}

/** Lookup helper for a single attention card's work context membership. */
export function workContextMembershipForItem(
  itemId: string,
  items: readonly AttentionItem[],
  agents: readonly AgentItem[] = [],
  options: BuildWorkThreadsOptions = {},
): AttentionItemWorkContextMembership | undefined {
  return indexAttentionItemWorkContexts(items, agents, options).get(itemId);
}

/**
 * Stable, collision-free assistant attachment id for a card's context
 * snapshot. Consolidated contexts use their Jira key / manual hub id (already
 * unique per context). Singleton contexts fall back to the primary label
 * (e.g. a Slack channel) which may repeat across cards, so the selected item
 * id is appended to disambiguate them. The id is stable across re-renders for
 * a given item.
 */
export function radarAttachmentIdForSnapshot(
  snapshot: AttentionItemContextSnapshot,
  item: AttentionItem,
): string {
  if (snapshot.isManual) return snapshot.thread.id;
  if (/^[A-Z]{2,}-\d+$/.test(snapshot.thread.id)) return snapshot.thread.id;
  return `${snapshot.thread.id}:${item.id}`;
}

function compareRecency(a: WorkThread, b: WorkThread): number {
  return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
}

export function sortWorkThreads(
  threads: readonly WorkThread[],
  mode: RadarThreadSortMode,
): WorkThread[] {
  const copy = [...threads];
  if (mode === 'recency') {
    return copy.sort(compareRecency);
  }
  if (mode === 'unread') {
    return copy.sort((a, b) => {
      if (a.hasUnread !== b.hasUnread) return a.hasUnread ? -1 : 1;
      if (a.priorityRank !== b.priorityRank) return a.priorityRank - b.priorityRank;
      return compareRecency(a, b);
    });
  }
  return copy.sort((a, b) => {
    if (a.priorityRank !== b.priorityRank) return a.priorityRank - b.priorityRank;
    return compareRecency(a, b);
  });
}

/**
 * Returns the count of consolidated WorkThreads visible in the Attention Radar.
 */
export function countRadarWorkThreads(items: AttentionItem[], agents: AgentItem[]): number {
  return buildWorkThreads(radarActionableItems(items), agents).length;
}
